<template>
  <q-card
    class="bg-transparent"
    :class="{
      'row justify-center': !isMobileView,
      'rounded-borders-20': !square
    }"
    :square="square"
    :flat="flat"
  >
    <q-card-section
      :class="isMobileView ? 'q-gutter-md' : 'col row' + ' q-pb-md items-center q-gutter-x-lg'"
    >
      <site-selector-zones-selectbox
        v-if="showSiteSelector"
        :class="{
          'row q-mb-sm': isMobileView,
          'col': !isMobileView,
          'bg-white': !$q.dark.isActive,
          'hover-border-white': $q.dark.isActive,
          'hover-border-dark': !$q.dark.isActive
        }"
        class="text-capitalize stretch"
        :style="{
          border: $q.dark.isActive ? '1px solid rgba(255, 255, 255, 0.6)' : '1px solid rgba(0, 0, 0, 0.24)',
          'font-size': isMobileView ? '18px' : '20px',
          'padding-top': isMobileView ? '12px' : '10px',
          'padding-bottom': isMobileView ? '12px' : '10px',
        }"
        style="font-family: inherit; font-weight: normal; border-radius: 4px;"
        hide-icon
        :flat="$q.dark.isActive"
        size="lg"
        :ripple="false"
      />
      <q-select
        v-model="selectedMovie"
        :class="isMobileView ? 'row q-mb-sm' : 'col'"
        input-style="inputStyle"
        :options="allMovies"
        :bg-color="$q.dark.isActive ? undefined : 'white'"
        :disable="movies.loading"
        :loading="movies.loading"
        :style="isMobileView ? 'font-size: 18px;' : 'font-size: 20px;'"
        option-label="name"
        :popup-content-style="isMobileView ? 'font-size: 16px;' : 'font-size: 18px;'"
        map-options
        emit-value
      >
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            :class="scope.opt.displayMetaData && scope.opt.displayMetaData.classes"
            v-on="scope.itemEvents"
          >
            <q-item-section avatar>
              <q-img
                no-spinner
                no-transition
                width="30px"
                :src="scope.opt.posterImageSmallUrl"
              />
            </q-item-section>
            <q-item-section>
              <div class="row items-end q-gutter-sm">
                <div class="col-auto">
                  {{ scope.opt.name }}
                </div>
                <div class="col-auto q-mb-xs">
                  <rated
                    v-if="scope.opt.rating"
                    :rated="scope.opt.rating"
                    :rating-reason="scope.opt.ratingReason"
                    :size="'sm'"
                    :light-text="$q.dark.isActive"
                  />
                </div>
              </div>
              <q-item-label
                v-if="scope.opt.future"
                caption
              >
                {{ scope.opt.dateOfFirstShowing.i18n('dateUTC') }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              v-if="matchingShowingBadges(scope.opt).length > 0"
              side
            >
              <div class="row q-gutter-xs">
                <showing-badge
                  v-for="showingBadge in matchingShowingBadges(scope.opt)"
                  :key="showingBadge.id"
                  class="col-auto"
                  size="sm"
                  :showing-badge="showingBadge"
                  outline
                  :color="$q.dark.isActive ? 'white' : 'black'"
                />
              </div>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
      <q-select
        v-model="selectedShowing"
        :loading="publicShowingsForMovie.loading"
        :class="isMobileView ? 'row' : 'col'"
        :options="showDateSeparators ? currentShowingsWithDateSeparators : currentShowings"
        :disable="!selectedMovie || publicShowingsForMovie.loading"
        :bg-color="$q.dark.isActive ? undefined : 'white'"
        :style="isMobileView ? 'font-size: 18px;' : 'font-size: 20px;'"
        :option-label="showing => showing.siteTime"
        :popup-content-style="isMobileView ? 'font-size: 16px;' : 'font-size: 18px;'"
        :option-disable="showing => !showing.validOnlinePurchaseTime"
        map-options
        emit-value
        @update:model-value="checkoutShowing()"
      >
        <template #option="{ itemProps, opt, toggleOption }">
          <q-item
            v-if="opt.isSeparator"
            dense
            :class="['date-separator', 'inset-shadow', 'no-transition', opt.isToday && 'today', opt.isTomorrow && 'tomorrow']"
          >
            <q-item-section>
              <q-item-label class="text-white">
                <span
                  v-if="opt.isToday || opt.isTomorrow"
                  :class="[opt.isToday && 'text-weight-bold', opt.isTomorrow && 'text-weight-medium']"
                >{{ opt.isToday ? 'Today,' : 'Tomorrow,' }}</span>
                {{ opt.date }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-else
            v-bind="itemProps"
            :class="[showDateSeparators && 'with-border']"
            @click="toggleOption(opt)"
          >
            <q-item-section>
              <q-item-label :class="[showDateSeparators && 'q-pl-md']">
                <template v-if="!$_.isDate(opt.siteTime)">
                  {{ opt.siteTime }}
                </template>
                <template v-else>
                  {{ opt.siteTime.i18n(showDateSeparators ? 'shortWeekdayShortTime' : 'shortLong') }}
                </template>
              </q-item-label>
            </q-item-section>
            <q-item-section
              v-if="matchingShowingBadges(opt).length > 0"
              side
            >
              <div class="row q-gutter-xs">
                <showing-badge
                  v-for="showingBadge in matchingShowingBadges(opt)"
                  :key="showingBadge.id"
                  class="col-auto"
                  size="sm"
                  :showing-badge="showingBadge"
                  outline
                  :color="$q.dark.isActive ? 'white' : 'black'"
                />
              </div>
            </q-item-section>
            <q-tooltip v-if="!opt.validOnlinePurchaseTime">
              {{ opt.hasPurchaseEmbargo ? $t('showing.ticketsGoOnSale', { time: $d(opt.embargoPurchaseLiftedAt, 'medium') }) : $t('showing.noLongerAvailableForPurchaseOnline') }}
            </q-tooltip>
          </q-item>
        </template>
      </q-select>
      <div
        v-if="showButton"
        :class="isMobileView ? 'row items-center text-center' : 'col-auto'"
      >
        <q-btn
          :disable="!selectedMovie"
          :class="isMobileView ? 'col' : ''"
          size="lg"
          @click="checkoutShowing"
        >
          <div>
            <q-icon
              v-if="showIcon"
              color="white"
              name="confirmation_number"
              size="lg"
            />
            <div class="text-white">
              Buy Tickets
            </div>
          </div>
        </q-btn>
        <q-tooltip v-if="!selectedMovie">
          Please select a Movie to get started
        </q-tooltip>
      </div>
    </q-card-section>
  </q-card>
</template>

<script>
import { date } from 'quasar'
const { formatDate } = date

import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'
import Rated from 'src/modules/consumer/components/Rated'
import SiteSelectorZonesSelectbox from 'src/modules/consumer/components/site-selector/SiteSelectorZonesSelectbox'

export default {
  name: 'ShowtimeQuickSelector',
  components: {
    ShowingBadge,
    Rated,
    SiteSelectorZonesSelectbox
  },
  props: {
    showButton: {
      type: Boolean,
      required: false,
      default: true
    },
    square: {
      type: Boolean,
      required: false,
      default: true
    },
    flat: {
      type: Boolean,
      required: false,
      default: true
    },
    showIcon: {
      type: Boolean,
      required: false,
      default: true
    },
    titleClassId: {
      type: Number,
      required: false,
      default: null
    },
    showingBadgeIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    showSiteSelector: {
      type: Boolean,
      required: false,
      default: false
    },
    showDateSeparators: {
      type: Boolean,
      required: false,
      default: true
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      selectedMovie: null,
      selectedShowing: null
    }
  },
  computed: {
    currentShowings () {
      return [{ siteTime: this.$i18n.t('time.actions.selectDateAndTime'), value: null, disable: true }, ...(this.publicShowingsForMovie?.data || [])]
    },
    currentShowingsWithDateSeparators () {
      const showingsAndSeparators = []
      let previousDate = null
      _.each(this.currentShowings, (showing) => {
        const showingDate = _.isDate(showing.siteTime) ? showing.siteTime.format('YYYY-MM-DD') : false
        if (showingDate && showingDate !== previousDate) {
          showingsAndSeparators.push({
            date: showing.siteTime.i18n('weekdayMonthDay'),
            isSeparator: true,
            isToday: showingDate === this.currentSite.today,
            isTomorrow: showingDate === this.currentSite.tomorrow
          })
          previousDate = showingDate
        }
        showingsAndSeparators.push(showing)
      })
      return showingsAndSeparators
    },
    allMovies () {
      return [{ name: this.movies.loading ? `${this.$i18n.t('status.loading')}...` : this.$i18n.t('movie.actions.select'), value: null, disable: true }, ...(this.movies?.data || [])]
    },
    sitesExcludingLibrary () {
      return _(this.sites.data).filter({ isLibrary: false }).value()
    }
  },
  watch: {
    selectedMovie: function () {
      this.selectedShowing = null
    }
  },
  created () {
    _.extend(this, { formatDate })
  },
  methods: {
    checkoutShowing () {
      if (!this.selectedMovie) return

      if (!this.selectedShowing) {
        this.$router.push(this.selectedMovie.pagePath)
        return
      }

      this.$router.push(this.selectedShowing.checkoutPath)
    },
    matchingShowingBadges (showing) {
      if (!showing.showingBadges) return []

      return _.filter(showing.showingBadges.data, showingBadge => _.includes(this.showingBadgeIds, showingBadge.id))
    }
  },
  configuration: ['currentSite', 'sites'],
  models: {
    movies: {
      showingBadges: true,
      variables () {
        return {
          type: 'all-published',
          orderBy: 'magic',
          titleClassId: this.titleClassId,
          limit: 1000
        }
      }
    },
    publicShowingsForMovie: {
      showingBadges: true,
      movie: true,
      variables () {
        return {
          movieId: this.selectedMovie?.id,
          skip: !this.selectedMovie
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.q-btn.disabled {
  opacity: 1 !important;
}
::-webkit-scrollbar-button {
  display: block;
}
::-webkit-scrollbar-track {
  display: block;
}
.q-virtual-scroll__content::-webkit-scrollbar {
  display: block !important;
  visibility: visible;
}
.date-separator {
  background: $blue-grey-6;
  font-size: 16px;
  position: sticky;
  top: 0;
  z-index: 1;

  &.today {
    background: var(--q-primary);
  }
  &.tomorrow {
    background: var(--q-secondary);
  }
}
.q-item:not(:first-child) + .date-separator {
  margin-top: $space-y-base;
}
.with-border +  .with-border {
  border-top: 1px solid $separator-color;
}
/**Overwrite default q-dropdow-button hover effect to be same as q-select **/
.hover-border-white:hover {
  border-color: white !important;
}
.hover-border-dark:hover {
  border-color: black !important;
}

</style>
<style lang="scss">
/**Overwrite default q-dropdow-button style to be same as q-select **/
.hover-border-white > .q-focus-helper,
.hover-border-dark > .q-focus-helper{
  opacity: 0 !important;
}
.hover-border-white .q-icon,
.hover-border-dark .q-icon{
  font-size: 1.2em;
  opacity: 0.6;
}
.stretch{
  width: stretch;
  width: -webkit-fill-available;
  width: -moz-available;
}
</style>
